import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Textarea } from '@/shared/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'@/shared/components/ui/select';
import {
  Upload,
  FileText,
  FolderOpen,
  Eye,
  AlertTriangle,
  CheckCircle2,
  X,
  File,
  FileCode,
  Loader2 } from
'lucide-react';

import { oigAPI } from '@/shared/services/api';

export const NFeUpload = () => {const { t } = useTranslation("oig");
  const [selectedUnit, setSelectedUnit] = useState('02.892.407/0002-87');
  const [ambiente, setAmbiente] = useState('producao');
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [nfeConfig, setNfeConfig] = useState({ companies: [] });

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const data = await oigAPI.getConfig();
        setNfeConfig(data || { companies: [] });
      } catch (err) {
        console.error('Failed to fetch config:', err);
      }
    };
    fetchConfig();
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles((prev) => [...prev, ...droppedFiles.map((f) => ({
      name: f.name,
      size: f.size,
      type: f.type,
      rawFile: f,
      status: 'pending'
    }))]);
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selectedFiles.map((f) => ({
      name: f.name,
      size: f.size,
      type: f.type,
      rawFile: f,
      status: 'pending'
    }))]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const extractAccessKey = (content, filename) => {
    const source = `${content || ''} ${filename || ''}`;
    return source.match(/\b\d{44}\b/)?.[0] || null;
  };

  const processFiles = async () => {
    setIsProcessing(true);
    const processed = await Promise.all(files.map(async (file) => {
      try {
        const content = file.rawFile ? await file.rawFile.text() : '';
        const accessKey = extractAccessKey(content, file.name);
        if (!accessKey) {
          return { ...file, status: 'error', error: 'NF-e access key not found' };
        }
        const validation = await oigAPI.validateNFe(accessKey);
        if (!validation?.valid) {
          return { ...file, status: 'error', error: validation?.error || 'Invalid NF-e access key' };
        }
        await oigAPI.intakeNFe({
          nfe_access_key: accessKey,
          invoice_number: validation.nfe_info?.invoice_number,
          supplier_name: validation.nfe_info?.cnpj ? `CNPJ: ${validation.nfe_info.cnpj}` : selectedUnit,
          carrier: 'OIG Upload',
          items: [{ sku: 'NFE-UPLOAD', product_name: file.name, quantity: 1 }]
        });
        return { ...file, status: 'success' };
      } catch (err) {
        return { ...file, status: 'error', error: err.message || 'Upload failed' };
      }
    }));
    setFiles(processed);
      setIsProcessing(false);
  };

  return (
    <div className="space-y-6" data-testid="oig-nfe-upload">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk'] flex items-center gap-3">
            <Upload className="h-8 w-8 text-emerald-600" />{t("uploadDeNfe", "Upload de NF-e")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("geraoDeNota", "Gera\xE7\xE3o de Nota Fiscal Eletr\xF4nica por Upload de Arquivo")}

          </p>
        </div>
      </div>

      {/* Alert Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600" />
        <p className="text-sm text-amber-800">
          <span className="font-medium">{t("ateno", "Aten\xE7\xE3o!")}</span>{t("oSistemaEmissor", "O sistema emissor de NF-e est\xE1 no CNPJ:")}
          <span className="font-mono font-bold ml-1">{selectedUnit}</span>{t("eNoAmbiente", "e no ambiente:")}
          <span className="font-bold ml-1 uppercase">{ambiente === 'producao' ? 'PRODUÇÃO' : 'HOMOLOGAÇÃO'}</span>
        </p>
      </div>

      {/* Unit and Environment Selection */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">{t("unidade", "Unidade")}</label>
          <Select value={selectedUnit} onValueChange={setSelectedUnit}>
            <SelectTrigger className="w-[280px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {nfeConfig.companies && nfeConfig.companies.length > 0 ?
              nfeConfig.companies.map((c) =>
              <SelectItem key={c.id} value={c.cnpj}>
                    {c.cnpj} - {c.identificacao}
                  </SelectItem>
              ) :

              <SelectItem value={selectedUnit}>
                  {selectedUnit}
                </SelectItem>
              }
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">{t("ambiente", "Ambiente")}</label>
          <div className="flex gap-1">
            <Button
              variant={ambiente === 'homologacao' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setAmbiente('homologacao')}
              className={ambiente === 'homologacao' ? 'bg-gray-600' : ''}>{t("homologao", "HOMOLOGA\xC7\xC3O")}


            </Button>
            <Button
              variant={ambiente === 'producao' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setAmbiente('producao')}
              className={ambiente === 'producao' ? 'bg-blue-600' : ''}>{t("produo", "PRODU\xC7\xC3O")}


            </Button>
          </div>
        </div>
      </div>

      {/* Upload Area */}
      <Card className="card-elevated">
        <CardContent className="p-6">
          <div className="flex gap-3 mb-6">
            <Button
              className="bg-red-500 hover:bg-red-600 gap-2"
              onClick={() => setUploadModalOpen(true)}>
              
              <Upload className="h-4 w-4" />{t("carregueArquivos", "Carregue Arquivos")}

            </Button>
            <Button variant="outline" className="gap-2 text-red-600 border-red-300">
              <Eye className="h-4 w-4" />{t("verArquivo", "Ver Arquivo")}

            </Button>
          </div>

          {/* Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDragging ?
            'border-emerald-500 bg-emerald-50' :
            'border-gray-300 hover:border-gray-400'}`
            }
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}>
            
            <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 mb-2">{t("arrasteOsArquivos", "Arraste os arquivos aqui")}</p>
            <p className="text-sm text-muted-foreground mb-4">{t("formatosAceitosXml", "Formatos aceitos: XML, TXT (NF-e)")}

            </p>
            <input
              type="file"
              id="file-upload"
              className="hidden"
              multiple
              accept=".xml,.txt"
              onChange={handleFileSelect} />
            
            <label htmlFor="file-upload">
              <Button variant="outline" asChild>
                <span className="cursor-pointer">{t("selecionarArquivos", "Selecionar Arquivos")}</span>
              </Button>
            </label>
          </div>

          {/* File List */}
          {files.length > 0 &&
          <div className="mt-6">
              <h3 className="font-medium mb-3">{t("arquivosSelecionados", "Arquivos Selecionados (")}{files.length})</h3>
              <div className="space-y-2">
                {files.map((file, index) =>
              <div
                key={index}
                className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                
                    <div className="flex items-center gap-3">
                      <FileCode className="h-5 w-5 text-emerald-600" />
                      <div>
                        <p className="font-mono text-sm">{file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(file.size / 1024).toFixed(1)}{t("kb", "KB")}
                    </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {file.status === 'success' &&
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  }
                      {file.status === 'error' &&
                  <Badge variant="destructive">{file.error || t("erro", "Erro")}</Badge>
                  }
                      {file.status === 'pending' &&
                  <Badge variant="outline">{t("pendente", "Pendente")}</Badge>
                  }
                      <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFile(index)}>
                    
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
              )}
              </div>
            </div>
          }

          {/* Actions */}
          <div className="flex justify-between items-center mt-6 pt-6 border-t border-border">
            <Button
              variant="outline"
              className="gap-2 text-red-600 border-red-300">
              
              <FileText className="h-4 w-4" />{t("testarGeraoDo", "Testar gera\xE7\xE3o do REC")}

            </Button>
            <div className="flex gap-3">
              <Button variant="outline">{t("listagem", "\u25C0 Listagem")}

              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                onClick={processFiles}
                disabled={files.length === 0 || isProcessing}>
                
                {isProcessing ?
                <>
                    <Loader2 className="h-4 w-4 animate-spin" />{t("processando", "Processando...")}

                </> :

                <>
                    <FileText className="h-4 w-4" />{t("gerarNotasFiscais", "Gerar Notas Fiscais")}

                </>
                }
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload Modal */}
      {uploadModalOpen &&
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{t("modalUploadFiles", "Modal upload files")}</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setUploadModalOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">{t("carregueArquivos", "Carregue Arquivos")}</label>
                <div
                className="border-2 border-dashed rounded-lg p-6 text-center border-gray-300">
                
                  <p className="text-muted-foreground">{t("arrasteOsArquivos", "\u2601 Arraste os arquivos aqui")}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t("categoria", "Categoria:")}</label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Nada selecionado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nfe">{t("nfe", "NF-e")}</SelectItem>
                      <SelectItem value="cte">{t("cte", "CT-e")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t("notificarPorEmail", "Notificar por E-mail:")}</label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Nada selecionado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sim">{t("sim", "Sim")}</SelectItem>
                      <SelectItem value="nao">{t("no", "N\xE3o")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t("privacidade", "Privacidade:")}</label>
                  <Select defaultValue="departamento">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="departamento">{t("departamento", "Departamento")}</SelectItem>
                      <SelectItem value="publico">{t("pblico", "P\xFAblico")}</SelectItem>
                      <SelectItem value="privado">{t("privado", "Privado")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t("nomeDoArquivo", "Nome do arquivo:")}</label>
                  <Input placeholder="" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t("vencimento", "Vencimento:")}</label>
                  <Select defaultValue="vencimento">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vencimento">{t("vencimento", "Vencimento")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">{t("descrioDoArquivo", "Descri\xE7\xE3o do Arquivo:")}</label>
                <Textarea rows={3} />
              </div>

              <div className="flex items-center gap-4">
                <Button className="bg-emerald-600 hover:bg-emerald-700">{t("confirmaCarregamento", "Confirma carregamento")}

              </Button>
                <span className="text-muted-foreground">{t("ou", "ou")}</span>
                <Button variant="link" onClick={() => setUploadModalOpen(false)}>{t("cancelar", "Cancelar")}

              </Button>
              </div>

              <div className="flex items-center gap-2">
                <input type="checkbox" id="create-link" />
                <label htmlFor="create-link" className="text-sm">{t("crieLinkE", "Crie link e me envie por E-mail")}

              </label>
              </div>
            </CardContent>
            <div className="p-4 flex justify-end border-t">
              <Button variant="destructive" onClick={() => setUploadModalOpen(false)}>{t("close", "Close")}

            </Button>
            </div>
          </Card>
        </div>
      }
    </div>);

};
